import { graphql, navigate } from 'gatsby';
import memoize from 'lodash.memoize';
import React, { useRef } from 'react';

import { smoothScrollToRef } from '../components/Common/ScrollTo';
import GradientToCtaHeader from '../components/Heros/GradientToCtaHeader';
import LogosHeader from '../components/Heros/LogosHeader';
import StylizedHeadlineHeader from '../components/Heros/StylizedHeadlineHeader';
import Layout from '../components/Layouts/Layout';
import MarketingSeo from '../components/MarketingSeo';
import { headBuilder } from '../tools/headBuilder';
import { sectionBuilder } from '../tools/sectionBuilder';
import * as styles from './standardPage.module.scss';

const StandardPage = ({ data, pageContext }) => {
  let page = {};
  if (data.page) page = data.page;
  const scrollRef = useRef();

  const onCtaClick = (e) => {
    e.preventDefault();
    if (!page.headingCtaScrollsToForm && page.headingCtaUrl) {
      return navigate(page.headingCtaUrl);
    }

    smoothScrollToRef(scrollRef, null, 84);
  };

  const fields = {
    colorTheme: page.pageColorTheme,
    heading: page.heading,
    headingFontColor: page.headingFontColor,
    headingTheme: page.headingTheme,
    lede: page.headingIntroCopy,
    videoUrl: page.headingVideoUrl,
    ctas: page.headingCtAs,
    heroImage: page.headingImage,
    backgroundImage: page.headingBackgroundImage,
    backgroundColor: page.heroBackground,
    headingSize: page.headingSize,
    headingSuper: page.headingSuper,
    headingSuperIcon: page.headingSuperIcon,
  };

  const headers = {
    ContentfulSectionHeaderGradientToCta: <GradientToCtaHeader section={page.header} />,
    ContentfulSectionLogosHeader: <LogosHeader section={page.header} />,
    ContentfulSectionStylizedHeadlineHeader: <StylizedHeadlineHeader fields={page.header} />,
  };

  const heading = page.header ? headers[page.header.__typename] : headBuilder(page.headingStyle, fields, onCtaClick);
  const buildSections = memoize(sectionBuilder);
  const sections = buildSections(page, styles, '', pageContext, scrollRef, onCtaClick);

  let colorTheme;

  switch (page.pageColorTheme) {
    case 'dark':
      colorTheme = 'blue';
      break;
    case 'evergreen deep':
      colorTheme = 'evergreen deep';
      break;
    default:
      colorTheme = 'white';
  }

  return (
    <Layout
      background={colorTheme}
      displayCta={page.displayNavCta}
      pageMeta={page.pageMetaData}
      onCtaClick={onCtaClick}
      smoothScroll={page.slug !== 'spark/agenda'}
    >
      {page.pageBackgroundImage && (
        <div
          className={styles.pageBackgroundImage}
          style={{ '--backgroundImage': `url('${page.pageBackgroundImage.file?.url}')` }}
        />
      )}
      {heading}
      {sections}
    </Layout>
  );
};

export default StandardPage;

export const Head = ({ data: { page }, location }) => {
  const meta = page.pageMetaData;
  const structuredData = meta?.structuredData ? JSON.parse(meta.structuredData.internal.content) : null;

  return (
    <>
      <MarketingSeo meta={meta} slug={location.pathname} />
      {structuredData && <script type="application/ld+json">{JSON.stringify(structuredData)}</script>}
    </>
  );
};

export const query = graphql`
  query StandardPageQuery($slug: String!) {
    page: contentfulPageStandard(slug: { eq: $slug }) {
      ...StandardPage
    }
  }
`;
